import { useEffect, useRef, useState } from 'react'
import { loadGoogleMaps, makeOrthoLayer } from '../lib/maps'

// MapBoundsEditor: mapa Google com o ortomosaico (tiles) e um retângulo editável/
// arrastável que define os limites (bounds) do survey. Ao mexer, chama onChange com
// {north,south,east,west}. Reutilizado no wizard e na edição a partir da lista.
//
// O mapa é criado UMA vez por levantamento. Antes o efeito dependia também do status e
// o frame de WebSocket que muda processing→ready montava um segundo mapa por cima do
// vivo, com o retângulo órfão ainda disparando o onChange e o enquadramento refeito por
// cima do pan do operador. É a mesma guarda do mini mapa dos furos.
export default function MapBoundsEditor({ mapObj, mine, onChange, height = 'h-72' }) {
  const elRef = useRef(null)
  const gmapRef = useRef(null)
  const rectRef = useRef(null)
  const orthoRef = useRef(null)
  const nsRef = useRef(null)
  const listenerRef = useRef(null)
  const builtForRef = useRef(null) // id do mapa já montado neste div
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const [err, setErr] = useState(null)

  const center = () => {
    const b = mapObj?.bounds
    if (b && b.north != null) return { lat: (b.north + b.south) / 2, lng: (b.east + b.west) / 2 }
    if (mine && mine.lat != null && mine.lon != null) return { lat: Number(mine.lat), lng: Number(mine.lon) }
    return { lat: -23.5505, lng: -46.6333 }
  }

  const defaultBounds = () => {
    const b = mapObj?.bounds
    if (b && b.north != null) return b
    const c = center()
    return { north: c.lat + 0.008, south: c.lat - 0.008, east: c.lng + 0.008, west: c.lng - 0.008 }
  }

  useEffect(() => {
    let cancelled = false
    const key = mapObj?.id ?? 'new'
    if (builtForRef.current === key) return undefined
    loadGoogleMaps().then((gmaps) => {
      if (cancelled || !elRef.current || builtForRef.current === key) return
      builtForRef.current = key
      nsRef.current = gmaps
      const map = new gmaps.Map(elRef.current, {
        zoom: 15, center: center(), mapTypeId: 'hybrid',
        fullscreenControl: false, streetViewControl: false, mapTypeControl: false,
        backgroundColor: '#0b0f17',
      })
      gmapRef.current = map

      const b0 = defaultBounds()
      const rect = new gmaps.Rectangle({
        map,
        bounds: { north: b0.north, south: b0.south, east: b0.east, west: b0.west },
        editable: true, draggable: true,
        strokeColor: '#017f96', strokeWeight: 2, fillColor: '#017f96', fillOpacity: 0.06,
        zIndex: 5,
      })
      rectRef.current = rect
      map.fitBounds(rect.getBounds())

      const emit = () => {
        const bb = rect.getBounds()
        if (!bb) return
        const ne = bb.getNorthEast(), sw = bb.getSouthWest()
        onChangeRef.current?.({ north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() })
      }
      listenerRef.current = rect.addListener('bounds_changed', emit)
      emit() // emite o inicial
    }).catch(e => setErr(e.message))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapObj?.id])

  // O ortomosaico só existe depois do processamento: entra como camada quando o status
  // vira ready, sem recriar o mapa nem o retângulo já ajustado.
  useEffect(() => {
    const gmaps = nsRef.current, map = gmapRef.current
    if (!gmaps || !map || mapObj?.status !== 'ready' || orthoRef.current) return
    orthoRef.current = makeOrthoLayer(gmaps, mapObj, { clamp: false })
    map.overlayMapTypes.insertAt(0, orthoRef.current)
  }, [mapObj, mapObj?.status])

  useEffect(() => () => {
    const gmaps = nsRef.current, map = gmapRef.current
    if (listenerRef.current) gmaps?.event.removeListener(listenerRef.current)
    if (rectRef.current) { gmaps?.event.clearInstanceListeners(rectRef.current); rectRef.current.setMap(null) }
    if (map && orthoRef.current) {
      const idx = map.overlayMapTypes.getArray().indexOf(orthoRef.current)
      if (idx >= 0) map.overlayMapTypes.removeAt(idx)
    }
    if (map) gmaps?.event.clearInstanceListeners(map)
    gmapRef.current = null; orthoRef.current = null; builtForRef.current = null
  }, [])

  return (
    <div className={`relative w-full ${height} rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700`}>
      <div ref={elRef} className="absolute inset-0" />
      {err && <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 bg-slate-100 dark:bg-slate-800">{err}</div>}
    </div>
  )
}
